/**
 * TA-10891 #21: web handoff that opens a new member's 1:1 chat with the
 * person who invited them (web twin of the native `pending_inviter_id` flow in
 * Chats.tsx). ProjectInvitationPreviewScreen.web stashes the inviter on a
 * successful desktop email-invite join; AppStack consumes it once the member
 * is signed in. sessionStorage is web only — on native every call is a no-op.
 */
export const PENDING_INVITER_ID_KEY = 'pending_inviter_id';

const storage = (): Storage | undefined => {
  try {
    return globalThis.sessionStorage ?? undefined;
  } catch {
    return undefined;
  }
};

/** Stash the inviter for AppStack to pick up after sign-in. */
export const setPendingInviter = (inviterId: string | null | undefined) => {
  if (inviterId) storage()?.setItem(PENDING_INVITER_ID_KEY, inviterId);
};

/**
 * Consume the stashed inviter (once) and open the chat with them. Returns the
 * opened chat id, or undefined when there was nothing to do. Best-effort: a
 * failed chat creation is swallowed, the key is already consumed either way.
 */
export const openPendingInviterChat = async <Chat extends { id?: string }>({
  meId,
  createChat,
  openChat,
}: {
  meId: string | undefined;
  createChat: (inviterId: string) => Promise<Chat | null | undefined>;
  openChat: (chat: Chat) => void;
}): Promise<string | undefined> => {
  if (!meId) return undefined;
  const store = storage();
  const inviterId = store?.getItem(PENDING_INVITER_ID_KEY);
  if (!inviterId) return undefined;
  store?.removeItem(PENDING_INVITER_ID_KEY);
  if (inviterId === meId) return undefined;
  try {
    const chat = await createChat(inviterId);
    if (!chat?.id) return undefined;
    openChat(chat);
    return chat.id;
  } catch {
    return undefined;
  }
};
